import React, { useState } from "react";
import { Field, Form } from "react-final-form";
import ValidatedInput from "../../common/ValidatedInput";
import { useErrorHandler } from "react-error-boundary";
import { useMutation } from "react-query";
import { createErrors } from "../../../queries/helpers";
import PropTypes from "prop-types";
import { updateProfile } from "../../../queries/session";
import { OnChange } from "react-final-form-listeners";
import isEmpty from "lodash/isEmpty";
import {
  capitalLetterRegex,
  lowerCaseLetterRegex,
  numbersRegex,
  specialCharRegex,
} from "../helpers";

const ProfilePage = ({ currentUser, toast }) => {
  const handleError = useErrorHandler();
  const [lengthLink, setLengthLink] = useState("");
  const [specialCharLink, setSpecialCharLink] = useState("");
  const [capitalLetterLink, setCapitalLetterLink] = useState("");
  const [lowerLetterLink, setLowerLetterLink] = useState("");
  const [numberLink, setNumberLink] = useState("");
  const [lengthCheck, setLengthCheck] = useState("");
  const [specialCharCheck, setSpecialCharCheck] = useState("");
  const [capitalLetterCheck, setCapitalLetterCheck] = useState("");
  const [lowerLetterCheck, setLowerLetterCheck] = useState("");
  const [numberCheck, setNumberCheck] = useState("");

  const [passwordType, setPasswordType] = useState("password");
  const [passwordClass, setPasswordClass] = useState("");

  const handlePasswordVisibility = () => {
    if (passwordType === "text") {
      setPasswordType("password");
      setPasswordClass("");
    } else {
      setPasswordType("text");
      setPasswordClass("password-show");
    }
  };

  const validateSpecialChar = (value) => {
    if (specialCharRegex.test(value)) {
      setSpecialCharLink("text-link");
      setSpecialCharCheck("icon-check ml-1");
    } else {
      setSpecialCharLink("");
      setSpecialCharCheck("");
    }
  };

  const validateCapitalLetter = (value) => {
    if (capitalLetterRegex.test(value)) {
      setCapitalLetterLink("text-link");
      setCapitalLetterCheck("icon-check ml-1");
    } else {
      setCapitalLetterLink("");
      setCapitalLetterCheck("");
    }
  };

  const validateNumber = (value) => {
    if (numbersRegex.test(value)) {
      setNumberLink("text-link");
      setNumberCheck("icon-check ml-1");
    } else {
      setNumberLink("");
      setNumberCheck("");
    }
  };
  const validateLowerLetter = (value) => {
    if (lowerCaseLetterRegex.test(value)) {
      setLowerLetterLink("text-link");
      setLowerLetterCheck("icon-check ml-1");
    } else {
      setLowerLetterLink("");
      setLowerLetterCheck("");
    }
  };

  const validateLength = (value) => {
    if (value.length >= 8) {
      setLengthLink("text-link");
      setLengthCheck("icon-check ml-1");
    } else {
      setLengthLink("");
      setLengthCheck("");
    }
  };
  const updateUser = useMutation(async (formData) => {
    await updateProfile(formData, currentUser.id);
  });

  const onSubmit = async (values) => {
    values["password_confirmation"] = values.password;
    return await updateUser
      .mutateAsync(values)
      .then(() => {
        toast.success("Your Profile updated successfully!");
      })
      .catch((error) => {
        toast.error([
          {
            title: "Failed to update your profile",
            body: `${error.response.data.errors
              .map((error) => error.detail)
              .join(", ")}`,
          },
        ]);
        return createErrors(
          error,
          "Failed to update your profile",
          handleError
        );
      });
  };
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={currentUser}
      validate={(values) => {
        const errors = {};
        if (!values.firstName) {
          errors.legalName = "Legal Name is Required";
        }
        if (!values.lastName) {
          errors.vat = "VAT number is Required";
        }
        if (!values.email) {
          errors.email = "Email is Required";
          if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
            errors.email = "Invalid email address";
          }
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="container mw-520">
            <h3 className="mb-4">Profile settings</h3>
            <div className="row row-xs">
              <div className="col-5">
                <Field
                  label={"First name"}
                  inputClassName="fin-control"
                  type="text"
                  name="firstName"
                  component={ValidatedInput}
                  placeholder="FirstName"
                />
              </div>
              <div className="col-7">
                <Field
                  label={"Last name"}
                  inputClassName="fin-control"
                  type="text"
                  name="lastName"
                  component={ValidatedInput}
                  placeholder="LastName"
                />
              </div>
              <div className="col-12">
                <Field
                  label={"Email"}
                  inputClassName="fin-control"
                  type="email"
                  name="email"
                  component={ValidatedInput}
                  placeholder="Email"
                />
              </div>
            </div>
            <h5 className="my-4">Change password</h5>
            <div className="fin-col">
              <div className={`password-wrapper ${passwordClass}`}>
                <a className="password-type-toggle">
                  <i
                    className={"icon-eye icon-md"}
                    onClick={() => handlePasswordVisibility()}
                  />
                </a>
                <Field
                  inputClassName="fin-control"
                  type={passwordType}
                  name="password"
                  component={ValidatedInput}
                  placeholder="Password"
                />
                <OnChange name="password">
                  {(value, previous) => {
                    if (value !== previous && !isEmpty(value)) {
                      validateSpecialChar(value);
                      validateNumber(value);
                      validateCapitalLetter(value);
                      validateLowerLetter(value);
                      validateLength(value);
                    } else {
                      setSpecialCharLink("");
                      setSpecialCharCheck("");
                      setCapitalLetterCheck("");
                      setCapitalLetterLink("");
                      setNumberLink("");
                      setNumberCheck("");
                      setLowerLetterLink("");
                      setLowerLetterCheck("");
                      setLengthLink("");
                      setLengthCheck("");
                    }
                  }}
                </OnChange>
              </div>
              <p className="text-small mt-3">
                Your new password must obey the following rules
              </p>
              <ul className="text-small pl-3">
                <li className={lengthLink}>
                  Be at least 8 characters long
                  <i className={lengthCheck} />
                </li>
                <li className={capitalLetterLink}>
                  Contain at least one capital letter
                  <i className={capitalLetterCheck} />
                </li>
                <li className={lowerLetterLink}>
                  Contain at least one lower letter
                  <i className={lowerLetterCheck} />
                </li>
                <li className={numberLink}>
                  Contain at least one number
                  <i className={numberCheck} />
                </li>
                <li className={specialCharLink}>
                  Contain at least one special character
                  <i className={specialCharCheck} />
                </li>
              </ul>
            </div>
            <div className="mt-45">
              <button
                disabled={submitting || pristine}
                id="submit"
                className="btn btn-sm btn-main"
              >
                Update profile
              </button>
              <button
                type="button"
                className="btn btn-sm btn-cancel"
                onClick={form.reset}
                disabled={submitting || pristine}
              >
                Reset
              </button>
            </div>
          </div>
        </form>
      )}
    />
  );
};

ProfilePage.propTypes = {
  currentUser: PropTypes.object.isRequired,
};

export default ProfilePage;
